import gatewayAxios from "src/lib/axios";
import Cookies from "js-cookie";
import { getSessionStorage } from "./storage";
import { API_AUTH_SERVICE_BACKEND, SERVER_DOMAIN, isLocalOrStagingEnv } from "src/config";

/**
 * Fetches temp token and appends _at to URL for gateway auth.
 * Returns the URL with _at param (or original URL on failure).
 * Use for iframe/src preview (doc, pdf, html) where cookies aren't sent.
 * @param {string} url - URL to add token to
 * @returns {Promise<string>} URL with _at param if gateway domain
 */
export const addTokenToUrl = async (url, options = {}) => {
  let newUrl = url;
  const isGatewayDomain = url?.includes(SERVER_DOMAIN) || url?.includes("localhost");
  if (isGatewayDomain) {
    const localOrStaging = isLocalOrStagingEnv();
    const localToken = localOrStaging ? window.sessionStorage.getItem("b2b_at_local") : null;
    // In prod `b2b_at` is httpOnly, so this always resolves to undefined there - that's expected.
    const readableToken = localToken || Cookies.get("b2b_at");
    const email = getSessionStorage("userEmail");

    // Default (no `force`): only call /token/tmp when we can actually read a bearer
    // token (local/staging). This is the original, already-proven-safe behavior used
    // by OAuth redirects (addTokenAndRedirect) and profile photos - those load via the
    // user's own browser (same-origin img/nav request), so the httpOnly `b2b_at` cookie
    // is already sent automatically in prod; they never needed `_at` there.
    //
    // `force: true` (opt-in): also attempt the call in prod with no readable token,
    // relying on gatewayAxios' withCredentials to send the httpOnly cookie for auth.
    // Reserved for the one case that genuinely can't rely on the browser sending
    // cookies at all: handing this URL to a third-party server (e.g. Microsoft's
    // Office Online viewer for docx/doc preview) that fetches it itself.
    if (readableToken || (options.force && !localOrStaging)) {
      try {
        const headers = { "x-user-id": email };
        if (readableToken) headers.Authorization = "Bearer " + readableToken;
        const res = await gatewayAxios.get(`${API_AUTH_SERVICE_BACKEND}/token/tmp`, { headers });
        if (res?.data?.token) {
          newUrl = `${url}${url?.includes("?") ? "&" : "?"}_at=${res.data.token}`;
        }
      } catch (err) {
        console.warn("addTokenToUrl:", err);
      }
    }
  }
  return newUrl;
};

export const addTokenAndRedirect = async (url) => {
  const newUrl = await addTokenToUrl(url);
  window.location.href = newUrl;
};

export const addTokenAndPopup = async (url) => {
  const width = 600;
  const height = 700;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;
  window.open(url, "OAuthPopup", `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes`);
};